"use client";

import { useLocale } from "@/context/LocaleContext";
import { useSearch } from "@/context/SearchContext";
import { I18nData } from "@/types/i18n";
import "./empty-state.scss";

// Fallbacks for accounts without these keys; keep in sync with "en" in
// apps/api/bin/migrations/0004_seed_search_error_i18n.py.
const FALLBACKS = {
  usage_exceeded:
    "You've made a lot of searches in a short time. Please wait a minute and try again.",
  failed: "Something went wrong with your search. Please try again.",
};

interface Props {
  i18nData?: I18nData;
}

/**
 * Shown where the results would be when a search fails, so a failure never reads as
 * "no journals match". Rate limiting (429) gets its own wording.
 */
export default function SearchErrorState({ i18nData }: Props) {
  const { searchError, currentSearchTerm, loading } = useSearch();
  const { locale } = useLocale();

  if (!searchError || !currentSearchTerm || loading) return null;

  const texts = i18nData?.[locale];
  const message =
    searchError === "usage_exceeded"
      ? (texts?.search_usage_exceeded ?? FALLBACKS.usage_exceeded)
      : (texts?.search_failed ?? FALLBACKS.failed);

  return (
    <div id="searchErrorState">
      <svg
        className="searchIcon"
        id="searchErrorIcon"
        width="100px"
        height="100px"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        stroke="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {/* role="alert": announced as soon as it appears, without moving focus (WCAG 4.1.3). */}
      <p id="searchErrorText" role="alert">
        {message}
      </p>
    </div>
  );
}
